import React from "react";
import { Link } from "react-router-dom";
import { Factory, Handshake, Cpu, FlaskConical, Gem, Eye, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";
import { IMG } from "@/data/images";

const PARTICIPANTS = [
  [Factory, "Licensed manufacturers"], [Handshake, "Dealers & brokers"],
  [Cpu, "Technology providers"], [FlaskConical, "Laboratories"],
  [Gem, "Jewelry manufacturers"], [Eye, "Distributors & retailers"],
];

const WORKFLOW = ["Submitted", "Documentation Review", "Accepted", "Identity Capture", "Optional Services", "Certificate Review", "Verified", "Released"];

export default function ForIndustry() {
  return (
    <div>
      <PageHero
        eyebrow="For Botswana Industry"
        title="You keep ownership. You control visibility."
        subtitle="Botswana Brilliance is an internationally traded diamond services platform working with licensed industry participants, not a diamond dealer or retailer. The lawful owner retains ownership and control of the diamond."
        image={IMG.craftHands}
      />

      <Section>
        <Reveal><Eyebrow>Who participates</Eyebrow></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PARTICIPANTS.map(([Icon, label], i) => (
            <Reveal key={label} delay={i * 0.06}>
              <div className="glass rounded-xl p-6 flex items-center gap-4 hover:border-bb-gold/25 transition-colors duration-500">
                <Icon className="h-6 w-6 text-bb-gold" strokeWidth={1} />
                <span className="text-bb-silver/85">{label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <div className="glass rounded-2xl p-8 md:p-12">
          <Reveal><Eyebrow>The Diamond Workflow</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-serif text-3xl md:text-4xl">A clear path from submission to release.</h2></Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-4">
            {WORKFLOW.map((step, i) => (
              <React.Fragment key={step}>
                <div className="flex items-center gap-3">
                  <span className="font-serif text-bb-gold text-lg">{`0${i + 1}`}</span>
                  <span className="text-sm text-bb-silver/80">{step}</span>
                </div>
                {i < WORKFLOW.length - 1 && <ArrowRight className="h-4 w-4 text-bb-silver/25" strokeWidth={1.3} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-2xl p-8">
              <h3 className="font-serif text-2xl">Data visibility, in your control</h3>
              <p className="text-sm text-bb-silver/65 mt-3 leading-relaxed">
                Participants keep ownership of their diamonds and control whether inventory is private, shared with selected
                buyers, or publicly presented. Nothing is exposed without your instruction.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Private", "Visible to BB only", "Approved buyers", "Publicly presented", "On hold", "Sold"].map((v) => (
                  <span key={v} className="rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-xs text-bb-silver/75">{v}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass rounded-2xl p-8 flex flex-col justify-between">
              <div>
                <h3 className="font-serif text-2xl">Your secure portal</h3>
                <p className="text-sm text-bb-silver/65 mt-3 leading-relaxed">
                  Register your company and licences, add authorised employees, submit diamonds or parcels, request laboratory
                  coordination and optional engraving, track service status, control visibility and download certificates.
                </p>
              </div>
              <Link to="/register" className="mt-6 btn-gold rounded-full px-7 py-3 text-sm w-fit">Register your company</Link>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
